/**
 * `Admin` — the operator dashboard. `spec.md` §6.9, §9.6, `docs/plan.md` T46,
 * `docs/design-direction.md` §5 (spacing/radii/borders), §8's screen contract and
 * §8.6's amp-faceplate spec-strip language (a control surface reapplies it rather
 * than an artist bio).
 *
 * ## Admin is an attribute of the session, and a non-admin sees NOTHING here
 *
 * `spec.md` §9.6: "There is no admin UI login; admin is an attribute of a Telegram
 * identity." This screen re-reads `session.user.isAdmin` on every render (never a
 * cached prop, never a route guard elsewhere) and, when it is `false`, returns `null`
 * — not a disabled panel, not a "you are not allowed" plate, nothing at all — and
 * redirects to the crate in an effect. Both halves matter: the redirect is a courtesy
 * for a person who followed a stale link, and the `null` render is the actual
 * boundary, because a disabled panel still leaks the shape of the admin surface to
 * whoever reads the DOM.
 *
 * ## `total: null` never becomes a fabricated number (§6.9, §8.8, §12.6)
 *
 * The one rule this whole file is built around. {@link formatProgress} (T37,
 * `app/states/Loading`) is the single place that decision is made — it returns a
 * string only when both `processed` and `total` are real, finite, consistent numbers
 * — and this screen never re-derives a percentage or a count itself. When it returns
 * `null` the job row prints the advancing `Ruler` and **zero digits**: not the job id,
 * not the processed count alone, nothing that could be mistaken for progress.
 *
 * ## The four REINDEX buttons are idempotent triggers, not a form
 *
 * A tap fires `POST /api/admin/reindex` and adopts the returned `jobId` as a `queued`
 * placeholder — honestly `total: null` / `progress: -1`, because that is what §6.9
 * guarantees a fresh job is until the indexer's first heartbeat, and minting a second
 * status request just to redraw the same unknown would be a wasted round trip.
 *
 * ## The job list polls every 3s, and STOPS — a real stop, not a guarded no-op
 *
 * The polling effect re-evaluates which jobs are non-terminal on every run and sets
 * up `setInterval` only when at least one exists; once every tracked job reaches
 * `done`/`failed`/`cancelled` the effect's cleanup fires and no further timer is ever
 * armed. That is the difference between "the network calls stop" and "the interval
 * keeps ticking but its callback becomes a no-op" — only the former is asserted by a
 * fetch-spy call count that stops growing, and only the former is honest about what
 * "stops polling" should mean.
 *
 * ## EVICT ALL is a two-step confirm, not a browser `confirm()`
 *
 * `confirm()` cannot be styled, cannot carry a Russian accessible name distinct from
 * its English print, and blocks the main thread. The arm/fire pattern here is the
 * same physical shape as the crate's `ОТМЕНИТЬ` undo window (§8.4): a first tap arms
 * a 4s window (visibly — the button's own print and colour change), and only a
 * second tap inside that window calls `POST /api/admin/cache/evict`. A tap after the
 * window lapses re-arms rather than firing, so a slow, hesitant operator gets a
 * second real question rather than an accidental eviction of the whole cache.
 *
 * ## Why this screen owns its own tiny HTTP client
 *
 * `lib/api.ts`'s `ApiClient` (T-whichever-shipped-it) has no admin methods — the nine
 * endpoints of §6.9 are an operator surface, not a listener-facing one, and extending
 * the shared client is out of this task's file list. {@link createAdminApiClient}
 * mirrors that module's envelope handling (the `{ok,data}` shape, the zod
 * `ResponseValidator` seam, `ApiError`/`toApiFailure` for one closed failure
 * vocabulary across the whole app) at the scale nine endpoints actually need, rather
 * than duplicating the session-refresh machinery an admin session does not exercise
 * mid-dashboard.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import {
  AdminCacheEvictResponseSchema,
  AdminIndexStateResponseSchema,
  AdminJobStatusResponseSchema,
  AdminReindexAcceptedSchema,
  AdminStatsResponseSchema,
  ErrSchema,
} from '@bl/shared';
import type {
  AdminCacheEvictResponse,
  AdminIndexStateResponse,
  AdminJobStatusResponse,
  AdminReindexAccepted,
  AdminStatsResponse,
  IndexJobState,
  ReindexKind,
} from '@bl/shared';

import type { ScreenProps } from '../app/router';
import { buildPath, navigate } from '../app/router';
import { formatProgress } from '../app/states/Loading';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import { Ruler } from '../components/primitives/Ruler';
import { SpecTable } from '../components/primitives/SpecTable';
import type { ApiFailure, ApiErrorInit, FetchLike, ResponseValidator } from '../lib/api';
import { ApiError, toApiFailure } from '../lib/api';
import { selectIsAdmin, useSessionStore } from '../store/session';

import styles from './admin.module.css';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** Latin label print — licensed caps (§4.3). */
export const ADMIN_TITLE = 'ADMIN';
export const ADMIN_SUBTITLE_RU = 'Индекс, кэш и очередь переиндексации';

export const INDEX_HEADING_RU = 'Индекс';
export const REINDEX_HEADING_RU = 'Переиндексация';
export const JOBS_HEADING_RU = 'Задачи';
export const CACHE_HEADING_RU = 'Кэш';
export const METRICS_HEADING_RU = 'Метрики';

export const RETRY_LABEL_RU = 'Ещё раз';
export const JOBS_EMPTY_RU = 'Задач пока не запускали';

export const EVICT_LABEL = 'EVICT ALL';
export const EVICT_CONFIRM_LABEL = 'CONFIRM';
export const EVICT_LABEL_RU = 'Очистить весь кэш';
export const EVICT_CONFIRM_LABEL_RU = 'Подтвердите очистку всего кэша';

/** The literal word the drift plate must print (§6.9's honesty rule, made visible). */
export const DRIFT_WORD = 'DRIFT';

const READING_INDEX_STATE_RU = 'Читаю состояние индекса';
const READING_STATS_RU = 'Читаю метрики';

/* ══ the four reindex kinds this screen exposes ═══════════════════════════════════ */

export const REINDEX_BUTTON_KINDS = ['incremental', 'backfill', 'peaks', 'covers'] as const;
export type ReindexButtonKind = (typeof REINDEX_BUTTON_KINDS)[number];

const REINDEX_KIND_LABELS: Readonly<Record<ReindexButtonKind, string>> = Object.freeze({
  incremental: 'INCREMENTAL',
  backfill: 'BACKFILL',
  peaks: 'PEAKS',
  covers: 'COVERS',
});

const REINDEX_KIND_LABELS_RU: Readonly<Record<ReindexButtonKind, string>> = Object.freeze({
  incremental: 'Запустить обычную дозагрузку новых записей',
  backfill: 'Запустить полную дозагрузку канала',
  peaks: 'Пересчитать пики громкости',
  covers: 'Обновить обложки',
});

const TERMINAL_JOB_STATES: ReadonlySet<IndexJobState> = new Set(['done', 'failed', 'cancelled']);

const JOB_POLL_MS = 3000;
const EVICT_ARM_TIMEOUT_MS = 4000;

/* ══ formatting helpers ════════════════════════════════════════════════════════════ */

/** `DD.MM.YYYY HH:MM`, fixed-width and locale-independent — no raw unix seconds. */
export function formatUnixDateTime(seconds: number | null): string | null {
  if (seconds === null) return null;
  const pad = (n: number): string => String(n).padStart(2, '0');
  const d = new Date(seconds * 1000);
  return (
    `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** `DRIFT +42` / `DRIFT −3` — the sign is never omitted, so a shrink reads as one. */
export function formatDrift(drift: number): string {
  const sign = drift > 0 ? '+' : drift < 0 ? '−' : '';
  return `${DRIFT_WORD} ${sign}${String(Math.abs(drift))}`;
}

/* ══ the admin HTTP client ═════════════════════════════════════════════════════════ */

export interface AdminApiClient {
  indexState(): Promise<AdminIndexStateResponse>;
  stats(): Promise<AdminStatsResponse>;
  reindex(kind: ReindexKind): Promise<AdminReindexAccepted>;
  jobStatus(jobId: number): Promise<AdminJobStatusResponse>;
  evictAll(): Promise<AdminCacheEvictResponse>;
}

export interface AdminClientDeps {
  readonly baseUrl?: string;
  readonly fetch?: FetchLike;
  /** Read fresh on every call — an admin session's token can still refresh mid-visit. */
  readonly getToken: () => string | null;
}

/** Mirrors `lib/api.ts`'s `toHttpError`: a real server code, or an honest fallback. */
function toAdminApiError(status: number, body: unknown): ApiError {
  const parsed = ErrSchema.safeParse(body);
  if (parsed.success) {
    const init: ApiErrorInit = {
      code: parsed.data.error.code,
      status,
      serverMessage: parsed.data.error.message,
    };
    if (parsed.data.error.details !== undefined) init.details = parsed.data.error.details;
    return new ApiError(init);
  }
  return new ApiError({
    code: status === 401 ? 'NO_SESSION' : 'BAD_RESPONSE',
    status,
    details: body,
  });
}

async function adminRequest<T>(
  deps: { baseUrl: string; fetch: FetchLike; getToken: () => string | null },
  method: string,
  path: string,
  validator: ResponseValidator<T>,
  body?: unknown,
): Promise<T> {
  const token = deps.getToken();
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json; charset=utf-8';
  if (token !== null) headers['authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await deps.fetch(`${deps.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'omit',
      cache: 'no-store',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    throw new ApiError({ code: 'NETWORK_FAILED', cause });
  }

  let payload: unknown;
  try {
    payload = response.status === 204 ? undefined : ((await response.json()) as unknown);
  } catch (cause) {
    if (response.ok) throw new ApiError({ code: 'BAD_RESPONSE', status: response.status, cause });
    throw toAdminApiError(response.status, undefined);
  }

  if (!response.ok) throw toAdminApiError(response.status, payload);

  if (
    typeof payload === 'object' &&
    payload !== null &&
    'ok' in payload &&
    payload.ok === true &&
    'data' in payload
  ) {
    const result = validator.safeParse((payload as { data: unknown }).data);
    if (!result.success) {
      throw new ApiError({ code: 'BAD_RESPONSE', status: response.status, details: result.error });
    }
    return result.data;
  }

  throw toAdminApiError(response.status, payload);
}

/** The nine-endpoint surface, reduced to the five this dashboard actually calls. */
export function createAdminApiClient(deps: AdminClientDeps): AdminApiClient {
  const baseUrl = deps.baseUrl ?? '';
  const fetchImpl =
    deps.fetch ?? ((input: string, init?: RequestInit) => globalThis.fetch(input, init));
  const base = { baseUrl, fetch: fetchImpl, getToken: deps.getToken };

  return {
    indexState: () =>
      adminRequest(base, 'GET', '/api/admin/index-state', AdminIndexStateResponseSchema),
    stats: () => adminRequest(base, 'GET', '/api/admin/stats', AdminStatsResponseSchema),
    reindex: (kind) =>
      adminRequest(base, 'POST', '/api/admin/reindex', AdminReindexAcceptedSchema, { kind }),
    jobStatus: (jobId) =>
      adminRequest(
        base,
        'GET',
        `/api/admin/reindex/${String(jobId)}`,
        AdminJobStatusResponseSchema,
      ),
    evictAll: () =>
      adminRequest(base, 'POST', '/api/admin/cache/evict', AdminCacheEvictResponseSchema, {
        all: true,
      }),
  };
}

/* ══ async section state ═══════════════════════════════════════════════════════════ */

interface Section<T> {
  readonly status: 'loading' | 'ready' | 'error';
  readonly data: T | null;
  readonly error: ApiFailure | null;
}

const SECTION_LOADING = <T,>(): Section<T> => ({ status: 'loading', data: null, error: null });

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

export interface AdminDeps {
  readonly client?: AdminApiClient;
  readonly session?: typeof useSessionStore;
  /** Epoch milliseconds. Injected so flood-wait comparisons are testable. */
  readonly now?: () => number;
  /** Overrides {@link JOB_POLL_MS} for a test that cannot wait 3 real seconds. */
  readonly pollMs?: number;
}

export type AdminProps = ScreenProps & { readonly deps?: AdminDeps };

export function Admin({ deps = {} }: AdminProps): JSX.Element | null {
  const session = deps.session ?? useSessionStore;
  const isAdmin = session(selectIsAdmin);
  const pollMs = deps.pollMs ?? JOB_POLL_MS;
  const now = deps.now ?? Date.now;

  const client = useMemo<AdminApiClient>(
    () => deps.client ?? createAdminApiClient({ getToken: () => session.getState().token }),
    [deps.client, session],
  );

  const [indexState, setIndexState] = useState<Section<AdminIndexStateResponse>>(SECTION_LOADING);
  const [stats, setStats] = useState<Section<AdminStatsResponse>>(SECTION_LOADING);
  const [jobs, setJobs] = useState<Partial<Record<ReindexButtonKind, AdminJobStatusResponse>>>({});
  const [pendingKind, setPendingKind] = useState<ReindexButtonKind | null>(null);
  const [reindexError, setReindexError] = useState<ApiFailure | null>(null);
  const [evictArmed, setEvictArmed] = useState(false);
  const [evictError, setEvictError] = useState<ApiFailure | null>(null);
  const [freedBytes, setFreedBytes] = useState<number | null>(null);

  const evictTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── redirect: a non-admin session never sees this screen (§9.6) ────────────── */
  useEffect(() => {
    if (!isAdmin) navigate(buildPath('crate'), { replace: true });
  }, [isAdmin]);

  /* ── index-state and stats: loaded once, admin-only ──────────────────────────── */
  const loadIndexState = useCallback(async (): Promise<void> => {
    setIndexState(SECTION_LOADING());
    try {
      const data = await client.indexState();
      setIndexState({ status: 'ready', data, error: null });
    } catch (error) {
      setIndexState({ status: 'error', data: null, error: toApiFailure(error) });
    }
  }, [client]);

  const loadStats = useCallback(async (): Promise<void> => {
    setStats(SECTION_LOADING());
    try {
      const data = await client.stats();
      setStats({ status: 'ready', data, error: null });
    } catch (error) {
      setStats({ status: 'error', data: null, error: toApiFailure(error) });
    }
  }, [client]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadIndexState();
    void loadStats();
  }, [isAdmin, loadIndexState, loadStats]);

  /* ── the four REINDEX triggers ────────────────────────────────────────────────── */
  const triggerReindex = useCallback(
    async (kind: ReindexButtonKind): Promise<void> => {
      setReindexError(null);
      setPendingKind(kind);
      try {
        const accepted = await client.reindex(kind);
        // §6.9: a fresh job is honestly total:null/progress:-1 until the indexer's
        // first heartbeat. No second request is spent just to redraw that same
        // unknown.
        const placeholder: AdminJobStatusResponse = {
          id: accepted.jobId,
          kind,
          state: 'queued',
          processed: 0,
          total: null,
          progress: -1,
          error: null,
          startedAt: null,
          finishedAt: null,
        };
        setJobs((prev) => ({ ...prev, [kind]: placeholder }));
      } catch (error) {
        setReindexError(toApiFailure(error));
      } finally {
        setPendingKind(null);
      }
    },
    [client],
  );

  /* ── the job poll: 3s, and a REAL stop once every job is terminal ────────────── */
  useEffect(() => {
    if (!isAdmin) return;

    /* Snapshot of the non-terminal jobs at the moment this effect (re-)armed — the
       effect depends on `jobs`, so a status landing in `setJobs` tears this interval
       down and re-arms a fresh one against the new snapshot. Once every tracked job
       is terminal, `activeKinds` is empty and NO interval is created at all — a real
       stop, not a callback that quietly stops doing anything (acceptance 5). */
    const activeKinds = (Object.keys(jobs) as ReindexButtonKind[]).filter((kind) => {
      const job = jobs[kind];
      return job !== undefined && !TERMINAL_JOB_STATES.has(job.state);
    });
    if (activeKinds.length === 0) return;

    const tick = async (): Promise<void> => {
      for (const kind of activeKinds) {
        const job = jobs[kind];
        if (job === undefined) continue;
        try {
          const status = await client.jobStatus(job.id);
          setJobs((prev) => ({ ...prev, [kind]: status }));
        } catch {
          /* A transient poll failure is not fatal — the next tick tries again. */
        }
      }
    };

    const id = setInterval(() => {
      void tick();
    }, pollMs);
    return () => {
      clearInterval(id);
    };
  }, [isAdmin, jobs, client, pollMs]);

  /* ── EVICT ALL: a genuine two-step confirm ───────────────────────────────────── */
  const disarmEvict = useCallback((): void => {
    setEvictArmed(false);
    if (evictTimer.current !== null) {
      clearTimeout(evictTimer.current);
      evictTimer.current = null;
    }
  }, []);

  useEffect(() => disarmEvict, [disarmEvict]);

  const onEvictPress = useCallback((): void => {
    if (!evictArmed) {
      setEvictArmed(true);
      evictTimer.current = setTimeout(() => {
        evictTimer.current = null;
        setEvictArmed(false);
      }, EVICT_ARM_TIMEOUT_MS);
      return;
    }
    disarmEvict();
    setEvictError(null);
    void (async (): Promise<void> => {
      try {
        const result = await client.evictAll();
        setFreedBytes(result.freedBytes);
        void loadStats();
      } catch (error) {
        setEvictError(toApiFailure(error));
      }
    })();
  }, [evictArmed, disarmEvict, client, loadStats]);

  /* ── nothing renders for a non-admin session — the actual boundary (§9.6) ────── */
  if (!isAdmin) return null;

  const nowSeconds = Math.floor(now() / 1000);
  const drift = indexState.data?.drift ?? 0;
  const floodWaitUntil = indexState.data?.floodWaitUntil ?? 0;

  return (
    <div>
      <Halftone as="header" className={styles.masthead}>
        <h1 className="t-display t-28">{ADMIN_TITLE}</h1>
        <p className={cx('t-ui', 't-15', styles.subtitle)}>{ADMIN_SUBTITLE_RU}</p>
      </Halftone>

      <div className={styles.section}>
        {/* ── index state ──────────────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={INDEX_HEADING_RU}>
          <DownbeatRule label={INDEX_HEADING_RU} />

          {indexState.status === 'loading' ? (
            <div className={styles.inlineStatus} role="status">
              <Ruler variant="loader" label={READING_INDEX_STATE_RU} />
              <span className={cx('t-mono', 't-13', styles.cyr)}>{READING_INDEX_STATE_RU}</span>
            </div>
          ) : null}

          {indexState.status === 'error' ? (
            <div className={styles.inlineStatus} role="alert">
              <span className={cx('t-mono', 't-13', styles.cyr, styles.errorLine)}>
                {`${indexState.error?.displayCode ?? ''} · ${indexState.error?.message ?? ''}`}
              </span>
              <button
                type="button"
                className={cx('t-ui', 't-15', styles.retryButton)}
                onClick={() => void loadIndexState()}
              >
                {RETRY_LABEL_RU}
              </button>
            </div>
          ) : null}

          {indexState.status === 'ready' && indexState.data !== null ? (
            <>
              <SpecTable
                rows={{
                  rows: indexState.data.rowCount,
                  'server total': indexState.data.serverAudioTotal,
                  'run state': indexState.data.runState,
                  'last success': formatUnixDateTime(indexState.data.lastSuccessAt),
                  'last error': indexState.data.lastError,
                  'flood wait':
                    floodWaitUntil > nowSeconds ? formatUnixDateTime(floodWaitUntil) : null,
                }}
              />
              {drift !== 0 ? (
                <p
                  className={cx('t-mono', 't-13', styles.drift)}
                  data-drift="true"
                  aria-label={`Расхождение с каналом: ${String(drift)}`}
                >
                  {formatDrift(drift)}
                </p>
              ) : null}
            </>
          ) : null}
        </section>

        {/* ── reindex triggers ─────────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={REINDEX_HEADING_RU}>
          <DownbeatRule label={REINDEX_HEADING_RU} />
          <div className={styles.reindexGrid} role="group" aria-label={REINDEX_HEADING_RU}>
            {REINDEX_BUTTON_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className={cx('t-mono', 't-11', styles.reindexButton)}
                aria-label={REINDEX_KIND_LABELS_RU[kind]}
                disabled={pendingKind === kind}
                data-kind={kind}
                onClick={() => {
                  void triggerReindex(kind);
                }}
              >
                {REINDEX_KIND_LABELS[kind]}
              </button>
            ))}
          </div>
          {reindexError === null ? null : (
            <p role="alert" className={cx('t-mono', 't-13', styles.cyr, styles.errorLine)}>
              {`${reindexError.displayCode} · ${reindexError.message}`}
            </p>
          )}
        </section>

        {/* ── live job list ────────────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={JOBS_HEADING_RU}>
          <DownbeatRule label={JOBS_HEADING_RU} />
          {Object.keys(jobs).length === 0 ? (
            <p className={cx('t-mono', 't-13', styles.cyr, styles.emptyLine)}>{JOBS_EMPTY_RU}</p>
          ) : (
            <ul className={styles.jobList} data-testid="job-list">
              {REINDEX_BUTTON_KINDS.map((kind) => {
                const job = jobs[kind];
                if (job === undefined) return null;
                const progress = formatProgress(job.processed, job.total);
                const terminal = TERMINAL_JOB_STATES.has(job.state);
                return (
                  <li
                    key={kind}
                    className={styles.jobRow}
                    data-job-kind={kind}
                    data-job-state={job.state}
                  >
                    <span className="t-mono t-11">{REINDEX_KIND_LABELS[kind]}</span>
                    <span className={cx('t-mono', 't-11', styles.jobState)}>
                      {job.state.toUpperCase()}
                    </span>
                    <span className={styles.jobProgress}>
                      {progress !== null ? (
                        <span className="t-mono t-13" data-progress="true">
                          {progress}
                        </span>
                      ) : terminal ? null : (
                        <Ruler
                          variant="loader"
                          label={`${REINDEX_KIND_LABELS[kind]} ${job.state}`}
                        />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* ── cache / EVICT ALL ────────────────────────────────────────────────── */}
        <section className={cx(styles.section, styles.cacheBlock)} aria-label={CACHE_HEADING_RU}>
          <DownbeatRule label={CACHE_HEADING_RU} />
          {stats.status === 'ready' && stats.data !== null ? (
            <SpecTable
              rows={{ 'cache bytes': stats.data.cacheBytes, 'wal bytes': stats.data.walBytes }}
            />
          ) : null}
          <button
            type="button"
            className={cx('t-mono', 't-11', styles.evictButton)}
            data-armed={evictArmed ? 'true' : 'false'}
            aria-pressed={evictArmed}
            aria-label={evictArmed ? EVICT_CONFIRM_LABEL_RU : EVICT_LABEL_RU}
            onClick={onEvictPress}
          >
            {evictArmed ? EVICT_CONFIRM_LABEL : EVICT_LABEL}
          </button>
          {freedBytes === null ? null : (
            <p className={cx('t-mono', 't-13', styles.cyr, styles.freedLine)}>
              {`Освобождено байт: ${String(freedBytes)}`}
            </p>
          )}
          {evictError === null ? null : (
            <p role="alert" className={cx('t-mono', 't-13', styles.cyr, styles.errorLine)}>
              {`${evictError.displayCode} · ${evictError.message}`}
            </p>
          )}
        </section>

        {/* ── metrics summary ──────────────────────────────────────────────────── */}
        <section className={styles.section} aria-label={METRICS_HEADING_RU}>
          <DownbeatRule label={METRICS_HEADING_RU} />
          {stats.status === 'loading' ? (
            <div className={styles.inlineStatus} role="status">
              <Ruler variant="loader" label={READING_STATS_RU} />
              <span className={cx('t-mono', 't-13', styles.cyr)}>{READING_STATS_RU}</span>
            </div>
          ) : null}
          {stats.status === 'error' ? (
            <div className={styles.inlineStatus} role="alert">
              <span className={cx('t-mono', 't-13', styles.cyr, styles.errorLine)}>
                {`${stats.error?.displayCode ?? ''} · ${stats.error?.message ?? ''}`}
              </span>
              <button
                type="button"
                className={cx('t-ui', 't-15', styles.retryButton)}
                onClick={() => void loadStats()}
              >
                {RETRY_LABEL_RU}
              </button>
            </div>
          ) : null}
          {stats.status === 'ready' && stats.data !== null ? (
            <SpecTable
              rows={{
                users: stats.data.users,
                tracks: stats.data.tracks,
                plays: stats.data.plays,
                favorites: stats.data.favorites,
              }}
            />
          ) : null}
        </section>
      </div>
    </div>
  );
}
